import React, { useEffect, useRef } from 'react';
import {
  dateToYmd,
  formatDateDmy,
  formatDmyInput,
  parseDateDmy,
  todayInTanzania,
} from '../utils/travelDate';

const TravelDatePicker = ({
  value,
  onChange,
  required = false,
  classic = false,
  label,
}) => {
  const pickerRef = useRef(null);
  const today = todayInTanzania();
  const minDate = dateToYmd(today);
  const maxDate = dateToYmd(
    new Date(today.getFullYear(), today.getMonth(), today.getDate() + 120)
  );
  const parsedDate = parseDateDmy(value);
  const pickerValue = parsedDate ? dateToYmd(parsedDate) : '';
  const displayYmd = pickerValue || minDate;

  // Keep parent form state in sync when the UI shows today's default.
  useEffect(() => {
    if (!value && typeof onChange === 'function') {
      onChange(formatDateDmy(today));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only seed empty value once
  }, []);

  const openPicker = () => {
    const input = pickerRef.current;
    if (!input) {
      return;
    }

    if (typeof input.showPicker === 'function') {
      input.showPicker();
      return;
    }

    input.focus();
    input.click();
  };

  const handlePickerChange = (event) => {
    const selected = event.target.value;
    if (!selected) {
      return;
    }

    const [year, month, day] = selected.split('-').map(Number);
    onChange(formatDateDmy(new Date(year, month - 1, day)));
  };

  if (classic) {
    return (
      <div className="relative">
        {label ? (
          <span className="mb-1 block text-[11px] font-body font-extrabold uppercase tracking-wide text-[#E91E63]">
            {label}
          </span>
        ) : null}
        <button
          type="button"
          onClick={openPicker}
          className="flex w-full items-center justify-between rounded-xl border-2 border-[#F8BBD0] bg-white px-3 py-2.5 text-left outline-none transition focus:border-[#E91E63]"
        >
          <span className="font-body text-[13px] font-semibold text-[#111827]">
            {displayYmd}
          </span>
          <svg
            className="h-5 w-5 shrink-0 text-[#E91E63]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </button>
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none absolute h-0 w-0 opacity-0"
          value={pickerValue || minDate}
          min={minDate}
          max={maxDate}
          onChange={handlePickerChange}
        />
      </div>
    );
  }

  return (
    <div className="relative">
      <input
        type="text"
        name="date"
        value={value}
        onChange={(event) => onChange(formatDmyInput(event.target.value))}
        placeholder="DD/MM/YYYY"
        inputMode="numeric"
        autoComplete="off"
        maxLength={10}
        required={required}
        className="w-full rounded-lg border-2 border-superfeo-gray bg-superfeo-white p-2 pr-10 font-body text-xs text-superfeo-darkgray focus:border-superfeo-gold focus:outline-none sm:p-2.5 sm:text-sm"
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label="Open date picker"
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1 text-superfeo-pink transition-colors hover:bg-superfeo-lightpink/40 hover:text-superfeo-darkpink"
      >
        <svg
          className="h-4 w-4 sm:h-5 sm:w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </button>
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        className="pointer-events-none absolute h-0 w-0 opacity-0"
        value={pickerValue}
        min={minDate}
        max={maxDate}
        onChange={handlePickerChange}
      />
    </div>
  );
};

export default TravelDatePicker;
